import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ColourSelectorComponent,
  IconPickerComponent,
  NgCommonModule,
} from '@principle-theorem/ng-shared';
import {
  DEFAULT_MATERIAL_ICONS,
  IStatusIndicatorConfig,
  StatusIndicatorMode,
} from '@principle-theorem/principle-core/interfaces';
import {
  BLACK_AND_WHITE_PALETTE,
  EVENT_PALETTE,
} from '@principle-theorem/shared';
import { StatusIndicatorDisplayComponent } from '../status-indicator-display/status-indicator-display.component';

enum IndicatorTab {
  Character = 0,
  Icon = 1,
}

@Component({
  selector: 'pr-status-indicator-picker',
  templateUrl: './status-indicator-picker.component.html',
  styleUrls: ['./status-indicator-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatMenuModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    ColourSelectorComponent,
    IconPickerComponent,
    StatusIndicatorDisplayComponent,
  ],
  host: { class: 'flex' },
})
export class StatusIndicatorPickerComponent {
  config = input.required<IStatusIndicatorConfig>();
  disableOpacity = input(false);
  palette = input<string[]>(EVENT_PALETTE);
  configChange = output<Omit<IStatusIndicatorConfig, 'enabled'>>();
  disabled = input(false);
  selectedBackgroundColour = signal<string | undefined>(undefined);
  selectedColour = signal<string | undefined>(undefined);
  selectedCharacter = signal<string | undefined>(undefined);
  selectedIcon = signal<string | undefined>(undefined);
  selectedMode = signal<StatusIndicatorMode>(StatusIndicatorMode.Character);
  selectedTabIndex = computed(() =>
    this.selectedMode() === StatusIndicatorMode.Icon
      ? IndicatorTab.Icon
      : IndicatorTab.Character
  );
  characterColourPalette = BLACK_AND_WHITE_PALETTE;
  icons = DEFAULT_MATERIAL_ICONS;

  constructor() {
    effect(() => {
      const config = this.config();

      this.selectedBackgroundColour.set(config.backgroundColour);
      this.selectedColour.set(config.colour ?? undefined);
      this.selectedCharacter.set(config.character ?? undefined);
      this.selectedIcon.set(config.icon ?? undefined);
      this.selectedMode.set(config.mode);
    });
  }

  characterChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const graphemes = Array.from(target.value);
    const character = graphemes.length > 0 ? graphemes[0] : '';
    this.selectedCharacter.set(character);

    if (target.value !== character) {
      target.value = character;
    }
  }

  backgroundColourChange(colour: string | undefined): void {
    if (colour) {
      this.selectedBackgroundColour.set(colour);
    }
  }

  colourChange(colour: string | undefined): void {
    this.selectedColour.set(colour);
  }

  iconSelect(icon: string): void {
    this.selectedIcon.set(icon);
  }

  updateMode(index: number): void {
    this.selectedMode.set(
      index === IndicatorTab.Icon
        ? StatusIndicatorMode.Icon
        : StatusIndicatorMode.Character
    );
  }

  save(): void {
    const config = this.config();
    const mode = this.selectedMode();

    const hasCharacterChanged = this.selectedCharacter() !== config.character;
    const hasColourChanged = this.selectedColour() !== config.colour;
    const hasIconChanged = this.selectedIcon() !== config.icon;
    const hasModeChanged = mode !== config.mode;
    const hasBackgroundColourChanged =
      this.selectedBackgroundColour() !== config.backgroundColour;

    if (
      hasCharacterChanged ||
      hasColourChanged ||
      hasIconChanged ||
      hasBackgroundColourChanged ||
      hasModeChanged
    ) {
      this.configChange.emit({
        mode,
        character: this.selectedCharacter(),
        icon: this.selectedIcon(),
        colour: this.selectedColour(),
        backgroundColour:
          this.selectedBackgroundColour() ?? config.backgroundColour,
      });
    }
  }
}
